import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, X, Star } from 'lucide-react';
import { HERO_DATA } from '../data/mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Memoize scroll handler to prevent recreation on every render
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Results', path: '/results' },
    { name: 'The Method', path: '/method' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#111216]/95 backdrop-blur-lg shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/logo.png" 
              alt="Detailing Disciples" 
              className="h-16 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-200 relative group ${
                  isActive(link.path)
                    ? 'text-[#B8860B]'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#B8860B] to-[#C8960B] transform origin-left transition-transform duration-200 ${
                    isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                ></span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-1 text-sm">
              <Star className="w-4 h-4 fill-[#B8860B] text-[#B8860B]" />
              <span className="font-bold text-white">{HERO_DATA.rating}</span>
              <span className="text-gray-400">{HERO_DATA.reviewSources}</span>
            </div>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-[#B8860B] to-[#C8960B] hover:from-[#C8960B] hover:to-[#B8860B] text-[#111216] font-semibold shadow-lg shadow-[#B8860B]/20 hover:shadow-[#B8860B]/40 transition-all duration-300">
                Request a Quote
              </Button>
            </Link>
            <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`}>
              <Button variant="outline" className="border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold transition-all duration-300">
                <Phone className="w-4 h-4 mr-2" />
                {HERO_DATA.phone}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-colors px-4 py-2 rounded-lg ${
                    isActive(link.path)
                      ? 'text-[#B8860B] bg-[#B8860B]/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 px-4 space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <Star className="w-4 h-4 fill-[#B8860B] text-[#B8860B]" />
                  <span className="font-bold text-white">{HERO_DATA.rating}</span>
                  <span className="text-gray-400">{HERO_DATA.reviewSources}</span>
                </div>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button className="w-full bg-gradient-to-r from-[#B8860B] to-[#C8960B] hover:from-[#C8960B] hover:to-[#B8860B] text-[#111216] font-semibold">
                    Request a Quote
                  </Button>
                </Link>
                <a href={`tel:${HERO_DATA.phone.replace(/[^0-9]/g, '')}`} className="block">
                  <Button variant="outline" className="w-full border-2 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#111216] font-semibold">
                    <Phone className="w-4 h-4 mr-2" />
                    {HERO_DATA.phone}
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
